import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

/**
 * Expands recurring meetings into per-day instances within their date range.
 *
 * Extracted from MeetingComponent/CalendarView so both the meeting calendar and
 * the Calendar View page share one source of truth for recurrence.
 *
 * recurring_days uses 0=Monday..6=Sunday (backend convention); dayjs uses
 * 0=Sunday..6=Saturday, so we convert before comparing.
 *
 * @param {Array<object>} meetings - Raw meetings from the API.
 * @returns {Array<object>} Meetings with recurring entries expanded into instances.
 */
export function expandRecurringMeetings(meetings = []) {
  const allMeetings = [];

  meetings.forEach((meeting) => {
    if (
      meeting.is_weekly_recurring &&
      meeting.recurring_days &&
      meeting.recurring_days.length > 0
    ) {
      let cursor = dayjs(meeting.start_date);
      const endDate = dayjs(meeting.end_date);

      while (cursor.isSameOrBefore(endDate, 'day')) {
        const dayjsDay = cursor.day(); // 0 = Sunday
        const backendDay = dayjsDay === 0 ? 6 : dayjsDay - 1; // 0 = Monday

        if (meeting.recurring_days.includes(backendDay)) {
          allMeetings.push({
            ...meeting,
            start_date: cursor.format('YYYY-MM-DD'),
            isRecurringInstance: true,
            originalMeetingId: meeting.id,
            is_recurring: true,
          });
        }
        cursor = cursor.add(1, 'day');
      }
    } else if (meeting.is_recurring) {
      let cursor = dayjs(meeting.start_date);
      const endDate = dayjs(meeting.end_date);

      while (cursor.isSameOrBefore(endDate, 'day')) {
        allMeetings.push({
          ...meeting,
          start_date: cursor.format('YYYY-MM-DD'),
          isRecurringInstance: true,
          originalMeetingId: meeting.id,
        });
        cursor = cursor.add(1, 'day');
      }
    } else {
      allMeetings.push(meeting);
    }
  });

  return allMeetings;
}

/**
 * Normalises a meeting-category task into a calendar event shape so it can be
 * rendered next to Meetings-model entries.
 * @param {object} task - A meeting task (category='Communication' with a meeting-typed action item).
 * @returns {object} A meeting-like event.
 */
export function taskToMeetingEvent(task) {
  return {
    id: `task-${task.id}`,
    taskId: task.id,
    meeting_name: task.name,
    url: '',
    start_date: task.do_date,
    end_date: task.do_date,
    start_time: task.start_time,
    end_time: task.end_time,
    isTask: true,
    attendees: task.assignee ? [task.assignee] : [],
  };
}

/** The 24 hourly slots used by the day/week timelines. */
export function buildTimeSlots() {
  const slots = [];
  for (let i = 0; i < 24; i++) {
    slots.push(dayjs().startOf('day').add(i, 'hour'));
  }
  return slots;
}

/** All days shown in a month grid (includes leading/trailing days to fill weeks). */
export function buildMonthDays(currentDate) {
  const startOfMonth = currentDate.startOf('month');
  const startDate = startOfMonth.startOf('week');
  const endDate = startOfMonth.endOf('month').endOf('week');

  const days = [];
  let day = startDate;
  while (day.isSameOrBefore(endDate, 'day')) {
    days.push(day);
    day = day.add(1, 'day');
  }
  return days;
}

/** The 7 days of the week containing `currentDate`. */
export function buildWeekDays(currentDate) {
  const startOfWeek = currentDate.startOf('week');
  return Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));
}

/**
 * Returns the date range covered by a calendar mode, as YYYY-MM-DD strings,
 * matching what the /calendar endpoint expects.
 * @param {'day'|'week'|'month'} mode
 * @param {dayjs.Dayjs} currentDate
 */
export function rangeForMode(mode, currentDate) {
  if (mode === 'week') {
    return {
      date_from: currentDate.startOf('week').format('YYYY-MM-DD'),
      date_to: currentDate.endOf('week').format('YYYY-MM-DD'),
    };
  }
  if (mode === 'month') {
    return {
      date_from: currentDate.startOf('month').startOf('week').format('YYYY-MM-DD'),
      date_to: currentDate.endOf('month').endOf('week').format('YYYY-MM-DD'),
    };
  }
  return {
    date_from: currentDate.format('YYYY-MM-DD'),
    date_to: currentDate.format('YYYY-MM-DD'),
  };
}

/**
 * Colour key for a month-grid day: overdue (red), due today (orange),
 * has meetings (blue), or none.
 */
export const DAY_COLORS = {
  overdue: '#d32f2f',
  dueToday: '#ed6c02',
  meeting: '#1976d2',
};
